import { useState } from "react";
import { toast } from "sonner";
import { get, post, errMsg, dt, BASE } from "@/lib/api";
import { PageHeader, Card, StatusBadge, Table, Pager, Loading, useFetch } from "@/components/kit";

export function AuditLogs() {
  const [f, setF] = useState({ entity: "", action: "" });
  const [page, setPage] = useState(1);
  const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), page };
  const { data } = useFetch(() => get("/audit-logs", params), [JSON.stringify(params)]);
  return (
    <div className="space-y-4">
      <PageHeader title="Audit logs" subtitle="Immutable, append-only trail. No edit/delete API exists." />
      <div className="flex gap-2">
        <select data-testid="audit-entity-filter" className="fo-input h-9 w-auto" value={f.entity} onChange={(e) => setF({ ...f, entity: e.target.value })}><option value="">All entities</option>{["lead", "application", "document", "payout", "lender", "user", "dsa", "setting", "role", "webhook", "partner", "customer"].map((x) => <option key={x}>{x}</option>)}</select>
        <input className="fo-input h-9 max-w-xs" placeholder="Action contains…" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} />
      </div>
      {!data ? <Loading /> : <>
        <Table testid="audit-table" rows={data.items} cols={[{ k: "timestamp", h: "Time", r: (a) => dt(a.timestamp) }, { k: "user_name", h: "User" }, { k: "role", h: "Role" }, { k: "action", h: "Action" }, { k: "entity", h: "Entity" },
          { k: "old_value", h: "Old", r: (a) => <span className="block max-w-[180px] truncate font-mono text-[10px]">{JSON.stringify(a.old_value)}</span> },
          { k: "new_value", h: "New", r: (a) => <span className="block max-w-[220px] truncate font-mono text-[10px]">{JSON.stringify(a.new_value)}</span> }, { k: "ip", h: "IP" },
          { k: "device", h: "Device", r: (a) => <span className="block max-w-[120px] truncate text-[10px]">{a.device}</span> }]} />
        <Pager page={page} size={data.size} total={data.total} setPage={setPage} />
      </>}
    </div>
  );
}

export function Webhooks() {
  const { data, reload } = useFetch(() => get("/admin/webhooks"), []);
  const replay = async (e) => { try { const r = await post(`/admin/webhooks/${e.id}/replay`); toast.success(`Replay: ${r.data.status}`); reload(); } catch (er) { toast.error(errMsg(er)); } };
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <PageHeader title="Webhooks" subtitle={`Endpoint: POST ${BASE}/api/v1/webhooks/{provider} · Header X-Signature = HMAC-SHA256 hex of raw body · X-Event-Id for idempotency · auto-retry with backoff → dead-letter after 5 attempts.`} />
      <Table testid="webhooks-table" rows={data} cols={[{ k: "received_at", h: "Received", r: (e) => dt(e.received_at) }, { k: "provider", h: "Provider" }, { k: "event_id", h: "Event ID", r: (e) => <span className="font-mono text-xs">{e.event_id}</span> },
        { k: "p", h: "Payload", r: (e) => <span className="font-mono text-[10px]">{e.payload ? `${e.payload.event} → ${e.payload.status}` : "—"}</span> }, { k: "status", h: "Status", r: (e) => <StatusBadge value={e.status} /> },
        { k: "attempts", h: "Attempts" }, { k: "last_error", h: "Error" },
        { k: "a", h: "", r: (e) => ["FAILED", "DEAD_LETTER"].includes(e.status) && <button data-testid={`replay-${e.id}`} onClick={() => replay(e)} className="text-xs font-semibold text-blue-800">Replay</button> }]} />
    </div>
  );
}

export function Health() {
  const { data } = useFetch(() => get("/system/health"), []);
  if (!data) return <Loading />;
  return (
    <div className="space-y-6">
      <PageHeader title="System health" subtitle={`Environment: ${data.environment}`} />
      <div className="grid gap-4 md:grid-cols-3" data-testid="health-grid">
        {data.components.map((c) => <Card key={c.name}><div className="flex items-center justify-between"><p className="font-semibold">{c.name}</p><StatusBadge value={c.status} /></div><p className="mt-1 text-xs text-slate-500">{c.note || (c.latency_ms ? `${c.latency_ms} ms` : "")}</p></Card>)}
      </div>
      <Card title="Lender APIs"><Table rows={data.lenders} cols={[{ k: "name", h: "Lender" }, { k: "active", h: "Active", r: (l) => (l.active ? "Yes" : "No") }, { k: "status", h: "Status", r: (l) => <StatusBadge value={l.status || "UNKNOWN"} /> }, { k: "last_response_ms", h: "Response ms" }, { k: "last_success_at", h: "Last success", r: (l) => dt(l.last_success_at) }, { k: "last_error", h: "Last error" }]} /></Card>
    </div>
  );
}
